import { useState } from "react";
import { format } from "date-fns";
import { CalendarIcon, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import { SERVICES, INDUSTRIES } from "@/data/site";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

const budgets = ["Under ₹25k", "₹25k – ₹75k", "₹75k – ₹2L", "₹2L+", "Not sure yet"];

export default function LeadForm({ compact = false, source = "contact_form" }) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "",
    industry: "", service: "", budget: "", message: "",
  });
  const [date, setDate] = useState();
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const message = date
        ? `${form.message}\nPreferred date: ${format(date, "PPP")}`
        : form.message;
      await api.post("/leads", { ...form, message, source });
      setDone(true);
      toast.success("Thank you! We'll reach out within 24 hours.");
      setForm({ name: "", email: "", phone: "", company: "", industry: "", service: "", budget: "", message: "" });
      setDate(undefined);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
        data-testid="lead-form-success"
        className="grid place-items-center rounded-3xl border border-dq-success/30 bg-dq-success/5 p-10 text-center"
      >
        <CheckCircle2 className="h-14 w-14 text-dq-success" />
        <h3 className="mt-4 font-display text-2xl font-extrabold text-dq-ink">Request received!</h3>
        <p className="mt-2 max-w-sm text-dq-muted">
          Our team will get in touch within 24 hours to plan your growth. Talk soon!
        </p>
        <button onClick={() => setDone(false)} data-testid="lead-form-reset" className="btn-secondary mt-6">
          Send another request
        </button>
      </motion.div>
    );
  }

  const inputCls =
    "w-full rounded-2xl border border-dq-line bg-white px-4 py-3.5 text-sm text-dq-ink placeholder:text-dq-muted/70 transition-colors focus:border-dq-orange focus:outline-none focus:ring-2 focus:ring-dq-orange/20";

  return (
    <form onSubmit={submit} data-testid="lead-form" className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <input required placeholder="Full name *" value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="lead-name" />
        <input required type="tel" placeholder="Phone number *" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} data-testid="lead-phone" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <input required type="email" placeholder="Email address *" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} data-testid="lead-email" />
        <input placeholder="Business name" value={form.company} onChange={(e) => set("company", e.target.value)} className={inputCls} data-testid="lead-company" />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Select value={form.service} onValueChange={(v) => set("service", v)}>
          <SelectTrigger className="h-[50px] rounded-2xl border-dq-line bg-white px-4 text-sm data-[placeholder]:text-dq-muted/70" data-testid="lead-service">
            <SelectValue placeholder="Service needed" />
          </SelectTrigger>
          <SelectContent className="max-h-64">
            {SERVICES.map((s) => (
              <SelectItem key={s.slug} value={s.title}>{s.title}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={form.industry} onValueChange={(v) => set("industry", v)}>
          <SelectTrigger className="h-[50px] rounded-2xl border-dq-line bg-white px-4 text-sm data-[placeholder]:text-dq-muted/70" data-testid="lead-industry">
            <SelectValue placeholder="Your industry" />
          </SelectTrigger>
          <SelectContent className="max-h-64">
            {INDUSTRIES.map((s) => (
              <SelectItem key={s.title} value={s.title}>{s.title}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!compact && (
        <div className="grid gap-4 sm:grid-cols-2">
          <Select value={form.budget} onValueChange={(v) => set("budget", v)}>
            <SelectTrigger className="h-[50px] rounded-2xl border-dq-line bg-white px-4 text-sm data-[placeholder]:text-dq-muted/70" data-testid="lead-budget">
              <SelectValue placeholder="Budget range" />
            </SelectTrigger>
            <SelectContent>
              {budgets.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
            </SelectContent>
          </Select>

          <Popover>
            <PopoverTrigger asChild>
              <button type="button" data-testid="lead-date"
                className="flex h-[50px] items-center gap-2 rounded-2xl border border-dq-line bg-white px-4 text-sm text-dq-muted transition-colors hover:border-dq-ink">
                <CalendarIcon className="h-4 w-4 text-dq-orange" />
                {date ? <span className="text-dq-ink">{format(date, "PPP")}</span> : "Preferred call date"}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus />
            </PopoverContent>
          </Popover>
        </div>
      )}

      <textarea rows={compact ? 3 : 4} placeholder="Tell us about your project or goals..." value={form.message} onChange={(e) => set("message", e.target.value)} className={inputCls} data-testid="lead-message" />

      <button type="submit" disabled={loading} data-testid="lead-submit" className="btn-primary w-full text-base disabled:opacity-60">
        {loading ? "Sending..." : "Get My Free Consultation"} <ArrowRight className="h-4.5 w-4.5" />
      </button>
      <p className="text-center text-xs text-dq-muted">We respond within 24 hours. Your details are safe with us.</p>
    </form>
  );
}
